import React, {useEffect, useRef} from 'react';
import {
  easeInOut,
  positionAtProgress,
  bearing,
  makeTruckEl,
  makeDestEl,
} from '../../helpers/map.helper';

function animateTruckMove(marker, imgEl, fromLngLat, toLngLat, duration) {
  let rafId = null;
  let startTs = null;
  const coords = [fromLngLat, toLngLat];

  function frame(ts) {
    if (!startTs) startTs = ts;
    const t = Math.min((ts - startTs) / duration, 1);
    const progress = easeInOut(t);
    const {lngLat, deg} = positionAtProgress(coords, progress);
    marker.setLngLat(lngLat);
    imgEl.style.transform = `rotate(${deg - 90}deg)`;
    if (t < 1) rafId = requestAnimationFrame(frame);
  }

  rafId = requestAnimationFrame(frame);
  return () => {
    if (rafId) cancelAnimationFrame(rafId);
  };
}

function loadMaplibre(cb) {
  if (window.maplibregl) {
    cb(window.maplibregl);
    return;
  }

  const existing = document.querySelector('script[data-maplibre]');
  if (existing) {
    existing.addEventListener('load', () => cb(window.maplibregl));
    return;
  }

  // Inject CSS
  if (!document.querySelector('link[data-maplibre]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.setAttribute('data-maplibre', '1');
    link.href = '/maplibre-gl.css';
    document.head.appendChild(link);
  }

  // Inject JS
  const script = document.createElement('script');
  script.setAttribute('data-maplibre', '1');
  script.src = '/maplibre-gl.js';
  script.onload = () => cb(window.maplibregl);
  document.head.appendChild(script);
}

const TrackingMap = ({
  driverLat,
  driverLng,
  driverLabel,
  destLat,
  destLng,
  destLabel,
  dropsBeforeYou,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const truckMarkerRef = useRef(null);
  const truckImgRef = useRef(null);
  const stopAnimRef = useRef(null);
  // Always-fresh driver props for async map.on('load') callbacks
  const driverPropsRef = useRef(null);
  // Previous reported lat/lng — updated synchronously at the top of Effect 2
  // so it is always reliable regardless of async map load timing.
  const prevLatRef = useRef(null);
  const prevLngRef = useRef(null);

  const hasDest = destLat != null && destLng != null;
  const tooFarAway = dropsBeforeYou != null && dropsBeforeYou >= 4;
  const hasDriver =
    dropsBeforeYou != null &&
    !tooFarAway &&
    driverLat != null &&
    driverLng != null;

  driverPropsRef.current = {driverLat, driverLng, hasDriver, driverLabel};

  // Effect 1: Initialize map when destination or driver availability changes.
  // Does NOT depend on driverLat/driverLng — position updates are handled by Effect 2.
  useEffect(() => {
    if (!containerRef.current || !hasDest) return;

    let destroyed = false;

    if (stopAnimRef.current) {
      stopAnimRef.current();
      stopAnimRef.current = null;
    }
    truckMarkerRef.current = null;
    truckImgRef.current = null;
    // Reset prev-pos refs so Effect 2 skips the first position it sees after
    // map reinit (the truck will be placed at that position, not animated to it).
    prevLatRef.current = null;
    prevLngRef.current = null;

    loadMaplibre(maplibregl => {
      if (destroyed || !containerRef.current) return;

      // Read the latest driver props at the time the map actually loads
      const {
        driverLat: lat,
        driverLng: lng,
        hasDriver: hd,
        driverLabel: dlabel,
      } = driverPropsRef.current;

      const map = new maplibregl.Map({
        container: containerRef.current,
        style: {
          version: 8,
          sources: {
            osm: {
              type: 'raster',
              tiles: [
                'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
                'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
                'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
              ],
              tileSize: 256,
              attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
            },
          },
          layers: [{id: 'osm', type: 'raster', source: 'osm'}],
        },
        center: hd ? [lng, lat] : [destLng, destLat],
        zoom: 13,
        scrollZoom: false,
      });
      mapRef.current = map;
      map.addControl(new maplibregl.NavigationControl(), 'top-right');

      map.on('load', () => {
        if (destroyed) return;

        new maplibregl.Marker({element: makeDestEl()})
          .setLngLat([destLng, destLat])
          .setPopup(
            new maplibregl.Popup({offset: 12}).setText(
              destLabel || 'Destination'
            )
          )
          .addTo(map);

        if (!hd) {
          map.setZoom(14);
          return;
        }

        const bounds = new maplibregl.LngLatBounds();
        bounds.extend([lng, lat]);
        bounds.extend([destLng, destLat]);
        map.fitBounds(bounds, {padding: 60, maxZoom: 15, animate: false});

        const straightCoords = [
          [lng, lat],
          [destLng, destLat],
        ];
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {type: 'LineString', coordinates: straightCoords},
          },
        });
        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {'line-join': 'round', 'line-cap': 'round'},
          paint: {'line-color': '#0000FF', 'line-width': 5, 'line-opacity': 0},
        });

        const {wrapper, img: imgEl} = makeTruckEl();
        // Point truck toward destination initially
        const initialDeg = bearing([lng, lat], [destLng, destLat]);
        imgEl.style.transform = `rotate(${initialDeg - 90}deg)`;

        const truckMarker = new maplibregl.Marker({
          element: wrapper,
          anchor: 'center',
        })
          .setLngLat([lng, lat])
          .setPopup(
            new maplibregl.Popup({offset: 12}).setText(dlabel || 'Driver')
          )
          .addTo(map);

        truckMarkerRef.current = truckMarker;
        truckImgRef.current = imgEl;

        // Sync prev-pos refs with where the truck was actually placed so the
        // FIRST position change after map load animates from the right spot.
        prevLatRef.current = lat;
        prevLngRef.current = lng;

        fetch(
          `https://router.project-osrm.org/route/v1/driving/${lng},${lat};${destLng},${destLat}?overview=full&geometries=geojson`
        )
          .then(r => r.json())
          .then(data => {
            if (!mapRef.current || destroyed) return;
            map
              .getSource('route')
              .setData({type: 'Feature', geometry: data.routes[0].geometry});
          })
          .catch(() => {});
      });
    });

    return () => {
      destroyed = true;
      if (stopAnimRef.current) {
        stopAnimRef.current();
        stopAnimRef.current = null;
      }
      prevLatRef.current = null;
      prevLngRef.current = null;
      truckMarkerRef.current = null;
      truckImgRef.current = null;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [hasDest, hasDriver, destLat, destLng]); // eslint-disable-line

  // Effect 2: Animate truck only when driver lat/lng changes to a new value.
  useEffect(() => {
    // Always capture previous position BEFORE any early returns so the ref
    // stays current for the next call, regardless of whether we animate.
    const prevLat = prevLatRef.current;
    const prevLng = prevLngRef.current;
    prevLatRef.current = driverLat;
    prevLngRef.current = driverLng;

    if (!hasDriver || driverLat == null || driverLng == null) return;

    // First position seen after mount/reinit — truck was already placed by
    // map.on('load') at this position, nothing to animate.
    if (prevLat == null || prevLng == null) return;

    // Position hasn't actually changed
    if (prevLat === driverLat && prevLng === driverLng) return;

    const marker = truckMarkerRef.current;
    const imgEl = truckImgRef.current;
    // Map still loading — skip; truck will be placed at the latest position
    // once map.on('load') fires and syncs prevLatRef/prevLngRef.
    if (!marker || !imgEl) return;

    if (stopAnimRef.current) stopAnimRef.current();

    stopAnimRef.current = animateTruckMove(
      marker,
      imgEl,
      [prevLng, prevLat],
      [driverLng, driverLat],
      8000
    );
  }, [driverLat, driverLng]); // eslint-disable-line

  if (!hasDest) return null;

  return (
    <div
      ref={containerRef}
      style={{width: '100%', height: '350px', margin: '1.5rem 0'}}
    />
  );
};

export default TrackingMap;
